/**
 * OIDC Authentication Configuration for Cognito + Federate
 *
 * This configuration uses the industry-standard OIDC Authorization Code flow with PKCE.
 * The flow is:
 * 1. User visits the app
 * 2. react-oidc-context redirects to Cognito
 * 3. Cognito redirects to Federate for SSO
 * 4. User logs in with Amazon credentials
 * 5. Federate redirects back to Cognito
 * 6. Cognito redirects back to app with authorization code
 * 7. Library exchanges code for tokens (PKCE - no client secret needed)
 * 8. App can now make API calls with JWT tokens
 */

import { AuthProviderProps } from 'react-oidc-context';

// Cognito configuration
const COGNITO_REGION = 'us-west-2';
const USER_POOL_ID = 'us-west-2_tV4kLttXk';
const CLIENT_ID = '6pd0jvrs68sbhvms07iattke0n';
const COGNITO_DOMAIN = 'us-west-2tv4klttxk.auth.us-west-2.amazoncognito.com';

// Application URLs
const APP_URL = window.location.origin; // https://deventry.satalentcard.ags.aws.dev

// OIDC Authority (Cognito's OIDC discovery endpoint)
const AUTHORITY = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/${USER_POOL_ID}`;

/**
 * OIDC Configuration
 *
 * This configuration tells the library how to interact with Cognito.
 * The library handles all the OAuth flow, token management, and PKCE automatically.
 */
export const oidcConfig: AuthProviderProps = {
  // Core OIDC settings
  authority: AUTHORITY,
  client_id: CLIENT_ID,
  redirect_uri: APP_URL,
  post_logout_redirect_uri: APP_URL,

  // OAuth flow settings
  response_type: 'code', // Authorization Code flow
  scope: 'openid email profile', // Scopes we need

  // PKCE (Proof Key for Code Exchange) - security feature for SPAs
  // This eliminates the need for a client secret

  // Token management
  automaticSilentRenew: true, // Automatically refresh tokens in background
  loadUserInfo: true, // Load user profile from /userinfo endpoint

  // Monitoring and callbacks
  onSigninCallback: () => {
    // After successful login, clean up URL query params
    window.history.replaceState({}, document.title, window.location.pathname);
  },

  // Metadata (optional - library can discover this, but we specify for performance)
  metadata: {
    issuer: AUTHORITY,
    authorization_endpoint: `https://${COGNITO_DOMAIN}/oauth2/authorize`,
    token_endpoint: `https://${COGNITO_DOMAIN}/oauth2/token`,
    userinfo_endpoint: `https://${COGNITO_DOMAIN}/oauth2/userInfo`,
    end_session_endpoint: `https://${COGNITO_DOMAIN}/logout`,
    jwks_uri: `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/${USER_POOL_ID}/.well-known/jwks.json`,
  },
};

/**
 * Helper function to get the ID token for API calls
 *
 * API Gateway Cognito Authorizer validates ID tokens, which contain:
 * - User identity claims (sub, cognito:username, preferred_username, email, etc.)
 * - Custom attributes (custom:isManager, custom:firstName, etc.)
 *
 * Access tokens only have OAuth scopes and don't include custom attributes.
 */
export const getIdToken = (): string | null => {
  const oidcStorage = sessionStorage.getItem(`oidc.user:${AUTHORITY}:${CLIENT_ID}`);
  if (!oidcStorage) return null;

  try {
    return JSON.parse(oidcStorage).id_token;
  } catch {
    return null;
  }
};

/** Legacy function - kept for reference but backend uses ID tokens */
export const getAccessToken = (): string | null => {
  const oidcStorage = sessionStorage.getItem(`oidc.user:${AUTHORITY}:${CLIENT_ID}`);
  if (!oidcStorage) return null;

  try {
    return JSON.parse(oidcStorage).access_token;
  } catch {
    return null;
  }
};
